import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Mail, Lock, ShieldCheck, Sparkles, AlertCircle, Clock, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatDetail } from "@/lib/api";
import { MfaEnrollStep } from "@/components/auth/MfaEnrollStep";

export default function LoginPage({ kind = "admin" }) {
    const { user, loading, login, verifyMfa, logout, setUser } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    // MFA challenge state: when populated, the form switches to a 6-digit
    // OTP entry instead of email/password.
    const [mfaToken, setMfaToken] = useState("");
    const [mfaCode, setMfaCode] = useState("");
    const [enrollToken, setEnrollToken] = useState("");

    // Surface a friendly "session expired" notice when the api.js
    // interceptor bounced an in-app call to this login page. The flag is
    // a query param so it survives the navigation; once the user signs
    // back in we send them to `?next=` (their original page).
    const { expired, nextPath } = useMemo(() => {
        const sp = new URLSearchParams(loc.search);
        return { expired: sp.get("expired") === "1", nextPath: sp.get("next") || "" };
    }, [loc.search]);

    // Auto-redirect ONLY if the existing session matches the portal we're on.
    // Otherwise (cross-portal click — e.g. partner clicked "Admin login"),
    // show the login form so they can sign in as a different kind. The new
    // /auth/<kind>/login call will replace the existing JWT cookie + user state.
    useEffect(() => {
        if (!loading && user && user.ut === kind) {
            // If we have a saved `next` path, honour it. Otherwise the
            // role-default landing page.
            const fallback = kind === "admin" ? "/admin" : "/partner";
            nav(nextPath || fallback);
        }
    }, [user, loading, kind, nav, nextPath]);

    const sessionMismatch = !loading && user && user.ut !== kind;

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const res = await login(kind, email, password);
            if (res?.kind === "mfa") {
                setMfaToken(res.mfa_token);
                return;
            }
            if (res?.kind === "mfa_setup") {
                setEnrollToken(res.mfa_token);
                return;
            }
            const u = res.user;
            const fallback = u.ut === "admin" ? "/admin" : "/partner";
            nav(nextPath || fallback);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const submitMfa = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const u = await verifyMfa(mfaToken, mfaCode.replace(/\s/g, ""));
            const fallback = u.ut === "admin" ? "/admin" : "/partner";
            nav(nextPath || fallback);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
            setMfaCode("");
        } finally { setBusy(false); }
    };

    return (
        <div className="auth-bg min-h-screen flex flex-col">
            <div className="px-6 py-5 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">P</div>
                    <span className="font-bold tracking-tight text-slate-900">PayGateway</span>
                </Link>
                <div className="text-xs text-slate-500">
                    {kind === "admin" ? <Link to="/partner/login" className="hover:text-blue-600">Partner login →</Link>
                                      : <Link to="/admin/login" className="hover:text-blue-600">Admin login →</Link>}
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="w-full max-w-md">
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-blue-700 uppercase bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
                            <Sparkles className="w-3 h-3" /> {kind === "admin" ? "Admin Portal" : "Partner Portal"}
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight mt-3">Welcome back</h1>
                        <p className="text-sm text-slate-500 mt-1">Sign in to continue.</p>
                    </div>

                    {expired && !sessionMismatch && (
                        <div data-testid="session-expired-banner" className="mb-4 rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 flex items-start gap-2">
                            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                Your session expired. Please sign in again to continue where you left off.
                            </div>
                        </div>
                    )}

                    {sessionMismatch && (
                        <div data-testid="session-mismatch-banner" className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                You&apos;re currently signed in as <strong>{user.ut}</strong> ({user.email}).
                                Signing in here will switch sessions to <strong>{kind}</strong>.
                                <button
                                    type="button"
                                    data-testid="switch-session-logout"
                                    onClick={async () => { await logout(); }}
                                    className="ml-1 underline hover:text-amber-950">
                                    Or sign out first
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="card p-6">
                        {enrollToken ? (
                            <MfaEnrollStep mfaToken={enrollToken}
                                onCancel={() => { setEnrollToken(""); setErr(""); }}
                                onDone={(u) => { setUser(u); nav(nextPath || (u.ut === "admin" ? "/admin" : "/partner")); }} />
                        ) : mfaToken ? (
                            <form onSubmit={submitMfa} className="space-y-4">
                                <div className="flex items-center gap-2 text-slate-800 font-medium">
                                    <KeyRound className="w-4 h-4" /> Two-factor verification
                                </div>
                                <p className="text-xs text-slate-500 -mt-2">
                                    Enter the 6-digit code from your authenticator app, or use a backup code.
                                </p>
                                <div>
                                    <label className="label">Code</label>
                                    <input
                                        data-testid="login-mfa-code"
                                        autoFocus
                                        required
                                        autoComplete="one-time-code"
                                        inputMode="text"
                                        value={mfaCode}
                                        onChange={(e) => setMfaCode(e.target.value)}
                                        className="input tracking-widest text-lg text-center"
                                        placeholder="123456" />
                                </div>
                                {err && <div data-testid="login-mfa-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2.5">{err}</div>}
                                <button data-testid="login-mfa-submit" disabled={busy} type="submit" className="btn-primary w-full py-2.5 rounded-md font-medium flex items-center justify-center gap-2">
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify
                                </button>
                                <button
                                    type="button"
                                    data-testid="login-mfa-back"
                                    onClick={() => { setMfaToken(""); setMfaCode(""); setErr(""); }}
                                    className="w-full text-xs text-slate-500 hover:text-slate-800">
                                    ← Cancel and use a different account
                                </button>
                            </form>
                        ) : (
                            <>
                                <form onSubmit={submit} className="space-y-4">
                                    <div>
                                        <label className="label">Email</label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                            <input data-testid="login-email" required type="email" autoComplete="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input !pl-10" placeholder="you@business.in" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label">Password</label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                            <input data-testid="login-password" required type="password" autoComplete="current-password" name="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input !pl-10" placeholder="••••••••" />
                                        </div>
                                    </div>
                                    {err && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2.5">{err}</div>}
                                    <button data-testid="login-submit" disabled={busy} type="submit" className="btn-primary w-full py-2.5 rounded-md font-medium flex items-center justify-center gap-2">
                                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign in
                                    </button>
                                </form>
                            </>
                        )}
                    </div>

                    <div className="text-center mt-5 text-[11px] uppercase tracking-wider text-slate-400 flex items-center justify-center gap-1.5">
                        <ShieldCheck className="w-3 h-3" /> Encrypted JWT session
                    </div>
                </div>
            </div>
        </div>
    );
}
